import { uniq } from '@sama/micro-dash';
import { flatten, keyBy, omit, property } from 'lodash';
import {
  ShapeState,
  TagsState,
} from '../../../cross-team-shared/annotation/shared/state/shape-state';
import {
  PointType,
  ShapeType,
} from '../../../team-apis/otterdam/answers/shape-typings';
import { TaskWorkspaceState } from '../../h3ml/state/task-workspace-state';
import { DiffEntry, getDiff } from './get-diff';

export function getDynamicTagDiff(
  prevShape: TagsState | undefined,
  curShape: TagsState,
) {
  return getDiff(prevShape, curShape, {
    selectors: [
      {
        getNextLevel: (shape) => shape.dynamic_tags,
        keyedBy: 'nestedOutputName (ignored)',
      },
      {
        getNextLevel: (frameSpecificTags) =>
          keyBy(frameSpecificTags, property('frame_number')),
        keyedBy: 'frameNumber',
      },
    ],
  });
}

export function getKeyLocationDiff(
  prevShape: ShapeState | undefined,
  curShape: ShapeState,
) {
  return getDiff(prevShape, curShape, {
    selectors: [
      {
        getNextLevel: (shape) =>
          keyBy(
            shape.key_locations.map((location) =>
              omit(location, 'extrapolation'),
            ),
            property('frame_number'),
          ),
        keyedBy: 'frameNumber',
      },
    ],
    equalityDepthAtLastLevel: 1,
  });
}

export function getMleLocationDiff(
  prevShape: ShapeState | undefined,
  curShape: ShapeState,
) {
  // includes both key locations and MLE
  const updatedFrameNumbers = new Set(
    getDiff(prevShape, curShape, {
      selectors: [
        {
          getNextLevel: (shape) =>
            keyBy(shape.key_locations, property('frame_number')),
          keyedBy: 'frameNumber',
        },
      ],
    }).map((entry) => +entry.frameNumber),
  );

  return getDiff(prevShape, curShape, {
    selectors: [
      {
        getNextLevel: (shape) => {
          const mleLocations = getMleLocations(shape, updatedFrameNumbers);
          return keyBy(mleLocations, property('frameNumber'));
        },
        keyedBy: 'frameNumber',
      },
    ],
    equalityDepthAtLastLevel: 1,
  });
}

export function getShape(
  taskWorkspaceState: TaskWorkspaceState,
  shapeEntry: DiffEntry,
): ShapeState | undefined {
  // @ts-ignore -- fix me
  return taskWorkspaceState.outputAnnotationInfo[shapeEntry.outputName].shapes[
    +shapeEntry.shapeId
  ];
}

export function getChangedFrameNumbers(
  prevShape: ShapeState | undefined,
  curShape: ShapeState,
) {
  return uniq(
    [
      ...getDynamicTagDiff(prevShape, curShape),
      ...getKeyLocationDiff(prevShape, curShape),
      ...getMleLocationDiff(prevShape, curShape),
    ].map((entry) => +entry.frameNumber),
  );
}

function getMleLocations(
  shape: ShapeState<PointType, ShapeType>,
  frameNumbers: Set<number>,
) {
  return flatten(
    shape.key_locations
      .filter(
        (keyLocation) =>
          frameNumbers.has(keyLocation.frame_number) &&
          keyLocation.extrapolation?.points,
      )
      .map((keyLocation) =>
        keyLocation.extrapolation!.points.map((points, i) => ({
          frameNumber: keyLocation.frame_number + i + 1,
          points,
        })),
      ),
  );
}
